import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileSignature, RefreshCw, Save, Stethoscope } from 'lucide-react';
import {
  ProcedureIdSchema,
  UserIdSchema,
  type Procedure,
  type ProcedureId,
  type SaveAnesthesiaDraftInput,
  type SignAnesthesiaInput,
  type UserId,
} from '@datazo/contracts';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SignaturePad } from '@/components/SignaturePad';
import { DrawableVitalsChart } from '@/components/DrawableVitalsChart';
import { ApiClientError } from '@/shared/api/client';
import { usePageTitle } from '@/shared/store/page';
import { usePatientById } from '@/features/patients/api';
import { useProcedureById } from '@/features/procedures/api';
import { useUsersList } from '@/features/users/api';
import { useToast } from '@/shared/toast/toast-context';
import {
  ANESTHESIA_FORM_VERSION_KEY,
  CURRENT_ANESTHESIA_FORM_VERSION,
  MED_SEDACION_RE,
  chartVariantForData,
  humanizeKey,
  knownKeysForData,
  medicamentosSedacion,
  sectionsForData,
} from './form-fields';
import { useAnesthesiaByProcedure, useSaveAnesthesiaDraft, useSignAnesthesia } from './api';

/**
 * Página de Registro de Anestesia — formato nuevo (papel Endomedical vigente).
 *
 * Decisión del producto: el anestesiólogo llena lo que considere relevante, sin
 * campos obligatorios excepto la firma final. El formulario replica el papel
 * "REGISTRO DE ANESTESIA": datos del paciente/procedimiento (auto-rellenados),
 * medicamento(s) en sedación, técnica anestésica, la gráfica de signos vitales
 * dibujable a mano alzada, y observaciones.
 *
 * Versionado: cada registro guarda `formVersion` dentro de `freeFormData`. Los
 * registros firmados con el formato anterior (sin marca) se siguen viendo e
 * imprimiendo EXACTAMENTE como se llenaron; los nuevos usan este formato.
 *
 * Persistencia: todos los inputs escriben a un único `data` (Record<string,
 * string>) que se serializa como `freeFormData` (jsonb). El dibujo de la grilla
 * va en `graficaVitalesUrl`.
 */
export function AnesthesiaSignPage(): JSX.Element {
  usePageTitle('Registro de anestesia', 'Documentos clínicos · Firma del anestesiólogo');
  const params = useParams();
  const validatedId = isProcedureId(params['procedimientoId'])
    ? params['procedimientoId']
    : undefined;
  const procedureQuery = useProcedureById(validatedId);

  if (validatedId === undefined) {
    return <PageError title="Identificador de procedimiento inválido" />;
  }
  if (procedureQuery.isLoading) {
    return <PageSkeleton />;
  }
  if (procedureQuery.isError || procedureQuery.data === undefined) {
    return (
      <PageError
        title="No fue posible cargar el episodio"
        message={
          procedureQuery.isError
            ? (procedureQuery.error?.message ?? 'Error desconocido')
            : 'Episodio no encontrado'
        }
        onRetry={procedureQuery.isError ? () => void procedureQuery.refetch() : undefined}
      />
    );
  }
  return <AnesthesiaLoaded procedure={procedureQuery.data} />;
}

function isProcedureId(value: string | undefined): value is ProcedureId {
  if (value === undefined) return false;
  return ProcedureIdSchema.safeParse(value).success;
}

/** Tope de medicamentos de sedación que se pueden anotar. */
const MAX_MED_SEDACION = 6;

/** Índice mayor de medicamentoSedacionN con valor no vacío (0 si no hay). */
function maxMedSedacionIndex(data: Record<string, unknown>): number {
  let max = 0;
  for (const [k, v] of Object.entries(data)) {
    const m = MED_SEDACION_RE.exec(k);
    if (m !== null && typeof v === 'string' && v.trim() !== '') {
      max = Math.max(max, Number(m[1]));
    }
  }
  return max;
}

/** Edad en años cumplidos a partir de la fecha de nacimiento (YYYY-MM-DD). */
function edadDesdeNacimiento(fechaNacimiento: string): string {
  const nac = new Date(`${fechaNacimiento}T00:00:00`);
  if (Number.isNaN(nac.getTime())) return '';
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad -= 1;
  return edad >= 0 && edad <= 130 ? String(edad) : '';
}

/* -------------------------------------------------------------------------- */
/* Form principal                                                              */
/* -------------------------------------------------------------------------- */

interface LoadedProps {
  procedure: Procedure;
}

function AnesthesiaLoaded({ procedure }: LoadedProps): JSX.Element {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const patientQuery = usePatientById(procedure.pacienteId);
  const existingQuery = useAnesthesiaByProcedure(procedure.id);
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });

  // El anestesiólogo que firma es el ASIGNADO al procedimiento, no el usuario
  // autenticado (que puede ser admin operando la tablet). El cirujano se toma
  // del médico asignado al episodio.
  const anestesiologoAsignado = useMemo(() => {
    if (procedure.anestesiologoId === undefined) return null;
    const items = usersQuery.data?.items ?? [];
    return items.find((u) => u.id === procedure.anestesiologoId) ?? null;
  }, [usersQuery.data, procedure.anestesiologoId]);
  const medicoAsignado = useMemo(() => {
    const items = usersQuery.data?.items ?? [];
    return items.find((u) => u.id === procedure.medicoId) ?? null;
  }, [usersQuery.data, procedure.medicoId]);

  const signMutation = useSignAnesthesia();
  const draftMutation = useSaveAnesthesiaDraft();

  const [data, setData] = useState<Record<string, string>>({});
  const [graficaVitalesUrl, setGraficaVitalesUrl] = useState<string>('');
  const [firmaAnestesiologo, setFirmaAnestesiologo] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // Cuántos campos de "medicamento en sedación" mostrar (mín 1, máx MAX_MED_SEDACION).
  const [medSedacionCount, setMedSedacionCount] = useState<number>(1);

  const set = (key: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
      const v = e.target.value;
      setData((prev) => ({ ...prev, [key]: v }));
    };

  // Quita el medicamento `idx` y recompacta las claves (sin huecos).
  const removeMedSedacion = (idx: number): void => {
    setData((prev) => {
      const vals: string[] = [];
      for (let i = 1; i <= medSedacionCount; i += 1) {
        vals.push(prev[`medicamentoSedacion${i}`] ?? '');
      }
      vals.splice(idx, 1);
      const next = { ...prev };
      for (let i = 1; i <= medSedacionCount; i += 1) {
        const v = vals[i - 1];
        if (v !== undefined && v !== '') next[`medicamentoSedacion${i}`] = v;
        else delete next[`medicamentoSedacion${i}`];
      }
      return next;
    });
    setMedSedacionCount((n) => Math.max(1, n - 1));
  };

  // Rehidratar un BORRADOR preguardado (una sola vez): contenido libre + gráfica.
  const existingIsDraft =
    existingQuery.data !== undefined &&
    existingQuery.data !== null &&
    existingQuery.data.estado === 'borrador';
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    const rec = existingQuery.data;
    if (rec === undefined || rec === null || rec.estado !== 'borrador') return;
    draftLoadedRef.current = true;
    if (rec.freeFormData !== undefined) {
      const ff: Record<string, string> = {};
      for (const [k, v] of Object.entries(rec.freeFormData)) {
        if (typeof v === 'string' && k !== ANESTHESIA_FORM_VERSION_KEY) ff[k] = v;
      }
      setData((prev) => ({ ...prev, ...ff }));
      const n = maxMedSedacionIndex(ff);
      if (n > 1) setMedSedacionCount(Math.min(MAX_MED_SEDACION, n));
    }
    if (rec.graficaVitalesUrl !== undefined) setGraficaVitalesUrl(rec.graficaVitalesUrl);
  }, [existingQuery.data]);

  // Auto-relleno desde paciente + procedimiento (una sola vez, sin pisar lo que
  // el usuario ya escribió ni lo rehidratado del borrador). Espera a que el
  // paciente y el directorio de usuarios estén listos para resolver nombres.
  const autofillDoneRef = useRef(false);
  useEffect(() => {
    if (autofillDoneRef.current) return;
    const p = patientQuery.data;
    if (p === undefined) return;
    if (usersQuery.isLoading) return;
    // Si hay borrador, deja que se rehidrate primero.
    if (existingIsDraft && !draftLoadedRef.current) return;
    autofillDoneRef.current = true;
    const auto: Record<string, string> = {
      fecha: procedure.fecha,
      nombrePaciente: `${p.nombres} ${p.apellidos}`,
      identificacion: `${p.tipoDoc} ${p.documento}`,
      edad: edadDesdeNacimiento(p.fechaNacimiento),
      empresa: procedure.entidad ?? '',
      procedimiento: procedure.tipo,
      cirujano: medicoAsignado?.nombre ?? '',
      anestesiologo: anestesiologoAsignado?.nombre ?? '',
    };
    setData((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(auto)) {
        if (v !== '' && (next[k] === undefined || next[k] === '')) next[k] = v;
      }
      return next;
    });
  }, [
    patientQuery.data,
    usersQuery.isLoading,
    medicoAsignado,
    anestesiologoAsignado,
    existingIsDraft,
    procedure,
  ]);

  const anestesiologoUserId = useMemo<UserId | null>(() => {
    if (procedure.anestesiologoId === undefined) return null;
    const parsed = UserIdSchema.safeParse(procedure.anestesiologoId);
    return parsed.success ? parsed.data : null;
  }, [procedure.anestesiologoId]);

  const isAlreadySigned = procedure.docs.anestesia === 'firmado';
  const isTerminal = procedure.estado === 'cerrado' || procedure.estado === 'anulado';
  // Se habilita con el consentimiento firmado — la anestesia y la hoja de gasto
  // se diligencian en paralelo, sin importar el orden.
  const episodeIsOpen =
    procedure.docs.consentimiento === 'firmado' && !isAlreadySigned && !isTerminal;

  const firmaOk = firmaAnestesiologo !== null && firmaAnestesiologo.length >= 50;
  const userOk = anestesiologoUserId !== null;
  const canSubmit = episodeIsOpen && firmaOk && userOk && !signMutation.isPending;
  const canDraft = episodeIsOpen && userOk && !draftMutation.isPending && !signMutation.isPending;

  const disabledReason = ((): string | null => {
    if (!episodeIsOpen) {
      if (isAlreadySigned) return 'Este episodio ya tiene el registro de anestesia firmado.';
      if (isTerminal) return `El episodio está ${procedure.estado} — no se puede firmar.`;
      return 'El episodio aún no permite firmar la anestesia (firma consentimiento primero).';
    }
    if (!firmaOk) return 'Falta la firma del anestesiólogo.';
    if (!userOk)
      return 'El episodio no tiene un anestesiólogo asignado. Asígnalo en "Datos del procedimiento" antes de firmar.';
    return null;
  })();

  /** freeFormData saneado (sin vacíos) + marca de versión del formato. */
  const buildFreeForm = (): Record<string, string> => {
    const clean: Record<string, string> = {};
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === 'string' && v.trim() !== '') clean[k] = v;
    }
    clean[ANESTHESIA_FORM_VERSION_KEY] = CURRENT_ANESTHESIA_FORM_VERSION;
    return clean;
  };

  const submit = async (): Promise<void> => {
    setServerError(null);
    if (!canSubmit || anestesiologoUserId === null || firmaAnestesiologo === null) return;
    const input: SignAnesthesiaInput = {
      procedimientoId: procedure.id,
      anestesiologoId: anestesiologoUserId,
      firmaAnestesiologo,
      ...(graficaVitalesUrl !== '' ? { graficaVitalesUrl } : {}),
      freeFormData: buildFreeForm(),
    };
    try {
      await signMutation.mutateAsync(input);
      showToast({
        kind: 'success',
        title: 'Registro de anestesia firmado',
        description: `Episodio ${procedure.id} actualizado.`,
      });
      navigate(`/procedimientos/${procedure.id}`);
    } catch (err) {
      setServerError(buildErrorMessage(err));
      showToast({
        kind: 'danger',
        title: 'No fue posible firmar el registro',
        description: buildErrorMessage(err),
      });
    }
  };

  const saveDraft = async (): Promise<void> => {
    setServerError(null);
    if (!canDraft || anestesiologoUserId === null) return;
    const input: SaveAnesthesiaDraftInput = {
      procedimientoId: procedure.id,
      anestesiologoId: anestesiologoUserId,
      ...(graficaVitalesUrl !== '' ? { graficaVitalesUrl } : {}),
      freeFormData: buildFreeForm(),
    };
    try {
      await draftMutation.mutateAsync(input);
      showToast({
        kind: 'success',
        title: 'Borrador guardado',
        description: 'Puedes salir y volver a terminarlo antes de firmar.',
      });
    } catch (err) {
      setServerError(buildErrorMessage(err));
      showToast({
        kind: 'danger',
        title: 'No se pudo preguardar',
        description: buildErrorMessage(err),
      });
    }
  };

  // Si ya existe un registro firmado, mostramos un banner + los datos guardados
  // tal cual se llenaron (respetando su versión de formato), sin editar.
  if (isAlreadySigned && existingQuery.data !== undefined && existingQuery.data !== null) {
    const existing = existingQuery.data;
    const f = (existing.freeFormData ?? {}) as Record<string, string>;
    return (
      <div className="flex flex-col gap-5">
        <TopBar procedureId={procedure.id} />
        <Card className="p-5 border-success/40 bg-success-soft text-text">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <FileSignature className="w-5 h-5 text-success" />
              <div className="text-sm">
                <div className="font-bold">Registro de anestesia firmado</div>
                <div className="text-text-muted">
                  Episodio {procedure.id} ·{' '}
                  {existing.firmadoEn ? new Date(existing.firmadoEn).toLocaleString('es-CO') : ''}
                </div>
              </div>
            </div>
            <Button variant="secondary" asChild>
              <Link to={`/procedimientos/${procedure.id}/imprimir?doc=anestesia`}>
                <Download className="w-4 h-4" /> Descargar / Imprimir
              </Link>
            </Button>
          </div>
        </Card>
        <ReadOnlyFormView data={f} graficaVitalesUrl={existing.graficaVitalesUrl} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <TopBar procedureId={procedure.id} />

      {!episodeIsOpen && (
        <Card className="p-4 border-warning/40 bg-warning-soft text-amber-900 text-sm">
          {disabledReason}
        </Card>
      )}

      {/* Datos del paciente y del procedimiento (auto-rellenados, editables) */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Registro de anestesia</CardTitle>
          <CardDescription>
            Datos del paciente y del procedimiento. Se traen automáticamente — puedes ajustarlos.
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field id="fecha" label="Fecha" type="date" onChange={set('fecha')} value={data['fecha']} />
          <div className="md:col-span-2">
            <Field id="nombre" label="Nombre" onChange={set('nombrePaciente')} value={data['nombrePaciente']} />
          </div>
          <Field id="identificacion" label="Identificación" onChange={set('identificacion')} value={data['identificacion']} />
          <Field id="edad" label="Edad" onChange={set('edad')} value={data['edad']} />
          <Field id="empresa" label="Empresa" onChange={set('empresa')} value={data['empresa']} />
          <div className="md:col-span-2">
            <Field id="procedimiento" label="Procedimiento" onChange={set('procedimiento')} value={data['procedimiento']} />
          </div>
          <Field id="riesgo" label="Riesgo" placeholder="Ej. ASA II" onChange={set('riesgo')} value={data['riesgo']} />
          <Field id="cirujano" label="Cirujano" onChange={set('cirujano')} value={data['cirujano']} />
          <Field id="anestesiologo" label="Anestesiólogo" onChange={set('anestesiologo')} value={data['anestesiologo']} />
        </div>
      </Card>

      {/* Medicamento(s) utilizado(s) en sedación */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Medicamento utilizado en sedación</CardTitle>
          <CardDescription>
            Escribe el/los medicamento(s) usados en la sedación. Puedes agregar varios. Opcional.
          </CardDescription>
        </CardHeader>
        <div className="flex flex-col gap-3">
          {Array.from({ length: medSedacionCount }).map((_, i) => {
            const key = `medicamentoSedacion${i + 1}`;
            return (
              <div key={key} className="flex items-end gap-2">
                <div className="flex-1">
                  <Field
                    id={key}
                    label={`Medicamento ${i + 1}`}
                    placeholder="Ej. Propofol 200 mg IV"
                    onChange={set(key)}
                    value={data[key]}
                  />
                </div>
                {medSedacionCount > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMedSedacion(i)}
                    className="mb-0.5 px-3 py-2 rounded-md border border-border text-xs font-semibold text-danger hover:bg-danger-soft hover:border-danger"
                  >
                    Quitar
                  </button>
                )}
              </div>
            );
          })}
          {medSedacionCount < MAX_MED_SEDACION && (
            <button
              type="button"
              onClick={() => setMedSedacionCount((n) => Math.min(MAX_MED_SEDACION, n + 1))}
              className="self-start px-3 py-1.5 rounded-md border border-dashed border-border text-xs font-semibold text-primary hover:border-primary hover:bg-primary/5"
            >
              + Agregar otro medicamento
            </button>
          )}
        </div>
      </Card>

      {/* Técnica anestésica */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Técnica anestésica</CardTitle>
        </CardHeader>
        <TextArea
          id="tecnica-anestesica"
          label="Técnica anestésica"
          rows={4}
          onChange={set('tecnicaAnestesica')}
          value={data['tecnicaAnestesica']}
        />
      </Card>

      {/* Gráfica de signos vitales (formato nuevo) */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Gráfica de signos vitales</CardTitle>
          <CardDescription>
            Dibuja directamente sobre la grilla — mismo gesto que la firma. Arrastra los símbolos
            del costado a la gráfica (puedes poner cuantos quieras). Deshacer quita el último trazo
            o símbolo; Borrar dibujo limpia todo.
          </CardDescription>
        </CardHeader>
        <DrawableVitalsChart
          variant="v2"
          withSymbols
          value={graficaVitalesUrl}
          onChange={setGraficaVitalesUrl}
        />
      </Card>

      {/* Observaciones */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Observaciones</CardTitle>
        </CardHeader>
        <TextArea
          id="observaciones"
          label="Observaciones"
          rows={4}
          onChange={set('observaciones')}
          value={data['observaciones']}
        />
      </Card>

      {/* Firma */}
      <Card className="p-5">
        <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
          <CardTitle>Firma del anestesiólogo</CardTitle>
          <CardDescription>
            La firma es lo único obligatorio. Al firmar, el documento queda cerrado e inalterable.
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-5">
          <div>
            <SignaturePad onChange={setFirmaAnestesiologo} placeholder="Firma del anestesiólogo" />
            <div className="text-[11px] text-text-muted flex flex-col gap-0.5 mt-2">
              {anestesiologoAsignado !== null ? (
                <>
                  <div className="font-semibold text-text">{anestesiologoAsignado.nombre}</div>
                  <div>{anestesiologoAsignado.especialidad ?? 'Anestesiólogo'}</div>
                  {anestesiologoAsignado.registroProfesional !== undefined &&
                  anestesiologoAsignado.registroProfesional !== '' ? (
                    <div className="font-mono text-text-soft">
                      {anestesiologoAsignado.registroProfesional}
                    </div>
                  ) : (
                    <div className="text-warning">Sin RM registrado</div>
                  )}
                  <div className="text-text-muted">{anestesiologoAsignado.correo}</div>
                </>
              ) : usersQuery.isLoading ? (
                'Cargando anestesiólogo asignado…'
              ) : procedure.anestesiologoId === undefined ? (
                <div className="text-warning">
                  El episodio no tiene anestesiólogo asignado. Edítalo en "Datos del procedimiento"
                  antes de firmar.
                </div>
              ) : (
                <div className="text-warning">
                  El anestesiólogo asignado ({procedure.anestesiologoId}) no se encontró en la base
                  de usuarios.
                </div>
              )}
            </div>
            {anestesiologoUserId === null && (
              <div className="text-[11px] text-red-700 mt-1">
                Sin anestesiólogo asignado al procedimiento — no se puede firmar.
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            <div className="text-sm text-text">
              Cuando termines, presiona <strong>Firmar registro</strong>. La grilla, los textos y la
              firma se guardan juntos.
            </div>
          </div>
        </div>

        {serverError !== null && (
          <div className="mt-4 px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
            {serverError}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          {existingIsDraft ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
              <Save className="w-4 h-4" /> Borrador guardado — puedes seguir editando
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3 ml-auto">
            <Button variant="secondary" asChild>
              <Link to={`/procedimientos/${procedure.id}`}>Cancelar</Link>
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void saveDraft()}
              disabled={!canDraft}
              title="Guarda un borrador sin firmar para terminarlo después"
            >
              <Save className="w-4 h-4" />
              {draftMutation.isPending ? 'Guardando…' : 'Preguardar'}
            </Button>
            <Button
              type="button"
              onClick={() => void submit()}
              disabled={!canSubmit}
              title={disabledReason ?? undefined}
            >
              <FileSignature className="w-4 h-4" />
              {signMutation.isPending ? 'Firmando…' : 'Firmar registro'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Componentes auxiliares                                                     */
/* -------------------------------------------------------------------------- */

function TopBar({ procedureId }: { procedureId: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/procedimientos/${procedureId}`}>
          <ArrowLeft className="w-4 h-4" /> Volver al episodio
        </Link>
      </Button>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value?: string;
  type?: string;
  placeholder?: string;
  onChange(e: React.ChangeEvent<HTMLInputElement>): void;
}

function Field({ id, label, value, type, placeholder, onChange }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
        {label}
      </Label>
      <input
        id={id}
        type={type ?? 'text'}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={onChange}
        className="h-9 px-2.5 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

interface TextAreaProps {
  id: string;
  label: string;
  rows?: number;
  value?: string;
  onChange(e: React.ChangeEvent<HTMLTextAreaElement>): void;
}

function TextArea({ id, label, rows, value, onChange }: TextAreaProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
        {label}
      </Label>
      <textarea
        id={id}
        rows={rows ?? 2}
        value={value ?? ''}
        onChange={onChange}
        className="px-2.5 py-1.5 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

/**
 * Vista de solo lectura del registro guardado — reconstruye el contenido con
 * las secciones/etiquetas de SU versión de formato (v1 o v2), la gráfica en su
 * variante correcta, y los medicamentos de sedación. Los registros previos se
 * ven exactamente como se llenaron.
 */
function ReadOnlyFormView({
  data,
  graficaVitalesUrl,
}: {
  data: Record<string, string>;
  graficaVitalesUrl?: string;
}): JSX.Element {
  const valor = (k: string): string | null => {
    const v = data[k];
    return typeof v === 'string' && v.trim() !== '' ? v : null;
  };

  const secciones = sectionsForData(data)
    .map((sec) => ({
      titulo: sec.titulo,
      grupos: sec.grupos
        .map((g) => ({
          titulo: g.titulo,
          campos: g.campos
            .map((c) => ({ label: c.label, value: valor(c.key) }))
            .filter((c): c is { label: string; value: string } => c.value !== null),
        }))
        .filter((g) => g.campos.length > 0),
    }))
    .filter((s) => s.grupos.length > 0);

  const meds = medicamentosSedacion(data);
  const known = knownKeysForData(data);
  const otros = Object.entries(data).filter(
    ([k, v]) =>
      !known.has(k) &&
      !MED_SEDACION_RE.test(k) &&
      k !== ANESTHESIA_FORM_VERSION_KEY &&
      typeof v === 'string' &&
      v.trim() !== '',
  );

  const chartVariant = chartVariantForData(data);

  return (
    <Card className="p-5">
      <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
        <CardTitle>Contenido del registro</CardTitle>
        <CardDescription>Datos guardados al momento de la firma — solo lectura.</CardDescription>
      </CardHeader>

      {secciones.map((sec) => (
        <div key={sec.titulo} className="mb-4">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-text-muted mb-2 block">
            {sec.titulo}
          </Label>
          {sec.grupos.map((g, gi) => (
            <div key={gi} className="mb-2 last:mb-0">
              {g.titulo !== undefined && (
                <div className="text-[10px] uppercase tracking-wider text-text-muted/80 font-semibold mb-1">
                  {g.titulo}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.campos.map((c) => (
                  <div key={c.label}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
                      {c.label}
                    </div>
                    <div className="text-sm text-text whitespace-pre-wrap">{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {meds.length > 0 && (
        <div className="mb-4">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-text-muted mb-2 block">
            Medicamento utilizado en sedación
          </Label>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {meds.map((m, i) => (
              <li key={i} className="text-sm text-text whitespace-pre-wrap">
                {m}
              </li>
            ))}
          </ul>
        </div>
      )}

      {graficaVitalesUrl !== undefined && graficaVitalesUrl !== '' && (
        <div className="mb-4">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-text-muted mb-2 block">
            Gráfica de signos vitales
          </Label>
          <div className="border border-border rounded-md bg-white p-2">
            <DrawableVitalsChart value={graficaVitalesUrl} readOnly variant={chartVariant} />
          </div>
        </div>
      )}

      {otros.length > 0 && (
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-text-muted mb-2 block">
            Otros datos
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {otros.map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
                  {humanizeKey(k)}
                </div>
                <div className="text-sm text-text whitespace-pre-wrap">{String(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function PageSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <div className="h-48 rounded-lg bg-surface-alt animate-pulse" />
      <div className="h-96 rounded-lg bg-surface-alt animate-pulse" />
    </div>
  );
}

function PageError({
  title,
  message,
  onRetry,
}: {
  title: string;
  message?: string;
  onRetry?(): void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card className="p-6 text-center">
        <div className="text-base font-bold text-text">{title}</div>
        {message !== undefined && <div className="text-sm text-text-muted mt-2">{message}</div>}
        {onRetry !== undefined && (
          <Button onClick={onRetry} className="mt-4">
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        )}
      </Card>
    </div>
  );
}

function buildErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 409) return 'El registro ya fue firmado para este episodio.';
    if (err.status === 401) return 'Sesión expirada. Vuelve a iniciar sesión.';
    if (err.status === 422 || err.status === 400) return 'Datos inválidos. Revisa el formulario.';
    if (err.status >= 500) return 'Error del servidor. Inténtalo nuevamente.';
    return err.message;
  }
  return 'No fue posible procesar la solicitud.';
}

export default AnesthesiaSignPage;
